import React from "react";

interface FormFieldProps {
  icon?: React.ReactNode;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  type?: string;
  onClearError?: () => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  helperText?: string; // Add helper text prop
  showHelperText?: boolean; // Control when to show helper text
  isValid?: boolean; // Show if field is valid
}

export const FormField: React.FC<FormFieldProps> = ({
  icon,
  label,
  value,
  onChange,
  error,
  required = false,
  type = "text",
  onClearError,
  placeholder,
  helperText,
  showHelperText = false,
  isValid = false,
}) => {
  // For phone number validation
  const isPhoneField = type === "tel";
  const numericValue = isPhoneField ? value.replace(/\D/g, "") : value;
  const isPhoneValid = isPhoneField && numericValue.length >= 8 && numericValue.length <= 12;
  const showPhoneHelper = isPhoneField && !error && value.length > 0 && numericValue.length < 8;

  // Generate dynamic helper text for phone numbers
  const getPhoneHelperText = () => {
    if (!isPhoneField) return helperText;
    if (numericValue.length === 0) return "Phone number must be 8 to 12 digits";
    if (numericValue.length < 8) return `Need ${8 - numericValue.length} more digit${8 - numericValue.length === 1 ? "" : "s"} (8-12 numbers required)`;
    if (numericValue.length > 12) return "Phone number cannot exceed 12 digits";
    return "✓ Valid phone number";
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-600 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div className={`flex items-center border rounded-lg px-3 py-2 bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500 ${error ? "border-red-500" : "border-gray-300"}`}>
        <span className="mr-2 text-gray-400">{icon}</span>

        <input
          type={type === "tel" ? "tel" : type}
          value={value}
          onChange={(e) => {
            if (type === "tel") {
              // Only allow digits
              const numeric = e.target.value.replace(/\D/g, "");
              onChange(numeric);
            } else {
              onChange(e.target.value);
            }
          }}
          onFocus={() => {
            if (onClearError) onClearError();
          }}
          placeholder={placeholder}
          className="w-full bg-transparent outline-none text-gray-700 placeholder:text-[12px] placeholder:text-[#999] placeholder:opacity-100"
        />
      </div>

      {/* Error message - show only for actual errors */}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

      {/* Helper text for phone numbers */}
      {isPhoneField && !error && value && !isPhoneValid && <p className="text-blue-500 text-xs mt-1">{getPhoneHelperText()}</p>}

      {/* Character counter for phone */}
      {isPhoneField && value && <p className={`text-xs mt-1 ${numericValue.length >= 8 && numericValue.length <= 12 ? "text-green-600" : "text-gray-400"}`}></p>}

      {/* Generic helper text */}
      {!isPhoneField && showHelperText && helperText && !error && <p className="text-gray-500 text-xs mt-1">{helperText}</p>}
    </div>
  );
};
